/**
 * MP3 metadata and **frame-aligned lossless** trimming (§20).
 *
 * An MP3 file is an ID3 tag (optional) followed by a run of independently
 * decodable audio frames. Trimming losslessly means finding the frame
 * boundaries and copying a whole-frame range — never cutting inside a frame,
 * which would corrupt the stream — and carrying the ID3v2 tag along so the
 * title and artist survive (§13, §24). Nothing is decoded or re-encoded, so no
 * patented encoder is involved (§20).
 *
 * The frame walker is the substance here. Each frame begins with an 11-bit sync
 * word and a header that encodes the MPEG version, layer, bitrate and sample
 * rate; the frame's length in bytes is computed from those. A reader that
 * scanned for `0xFF` bytes without validating the header would resynchronise on
 * data that merely looks like a header — common inside the audio — and report a
 * wrong duration, so every candidate is validated before it is trusted.
 */
import { HandlerError } from '../contract.js';

export interface Mp3Info {
  readonly durationSec: number;
  readonly bitrateKbps: number | null;
  /** True when the file's frames vary in bitrate. */
  readonly variableBitrate: boolean;
  readonly sampleRate: number | null;
  readonly channels: number | null;
  /** ID3 tags: title, artist, album, … whichever were present. */
  readonly tags: Readonly<Record<string, string>>;
  /** Byte offset where the audio frames begin, past any ID3v2 tag. */
  readonly audioStart: number;
}

/* MPEG audio header tables. Index by the bits in the header. */
const MPEG_VERSION = [2.5, null, 2, 1] as const; // bits 19-20
const SAMPLE_RATES: Readonly<Record<string, readonly number[]>> = {
  '1': [44100, 48000, 32000], '2': [22050, 24000, 16000], '2.5': [11025, 12000, 8000],
};
// Layer III bitrates (kbps) for MPEG1 and MPEG2/2.5.
const BITRATES_L3_V1 = [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320];
const BITRATES_L3_V2 = [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160];

interface Frame { readonly offset: number; readonly length: number; readonly samples: number; readonly sampleRate: number; readonly bitrate: number; readonly channels: number }

/** Decode one frame header at `pos`, or null when it is not a valid frame. */
function readFrame(bytes: Uint8Array, pos: number): Frame | null {
  if (pos + 4 > bytes.length) return null;
  const b1 = bytes[pos]!, b2 = bytes[pos + 1]!, b3 = bytes[pos + 2]!, b4 = bytes[pos + 3]!;
  if (b1 !== 0xff || (b2 & 0xe0) !== 0xe0) return null;

  const version = MPEG_VERSION[(b2 >> 3) & 0x03];
  const layer = (b2 >> 1) & 0x03;          // 01 = Layer III
  if (version === null || layer !== 0x01) return null;

  const bitrateIndex = (b3 >> 4) & 0x0f;
  const sampleIndex = (b3 >> 2) & 0x03;
  if (bitrateIndex === 0 || bitrateIndex === 15 || sampleIndex === 3) return null;

  const table = version === 1 ? BITRATES_L3_V1 : BITRATES_L3_V2;
  const bitrate = table[bitrateIndex]!;
  const sampleRate = SAMPLE_RATES[String(version)]![sampleIndex]!;
  const padding = (b3 >> 1) & 0x01;
  const channels = ((b4 >> 6) & 0x03) === 3 ? 1 : 2;

  // Samples per frame: 1152 for MPEG1 Layer III, 576 for MPEG2/2.5.
  const samples = version === 1 ? 1152 : 576;
  const length = Math.floor((samples / 8 * bitrate * 1000) / sampleRate) + padding;
  if (length < 4) return null;

  return { offset: pos, length, samples, sampleRate, bitrate, channels };
}

/* ------------------------------------------------------------------ *
 * ID3
 * ------------------------------------------------------------------ */

/** Length of the ID3v2 tag at the start, or 0 when there is none. */
function id3v2Length(bytes: Uint8Array): number {
  if (bytes.length < 10 || ascii(bytes, 0, 3) !== 'ID3') return 0;
  // The size is 28 bits, seven per byte ("synchsafe") to avoid false syncs.
  const size = (bytes[6]! << 21) | (bytes[7]! << 14) | (bytes[8]! << 7) | bytes[9]!;
  const footer = (bytes[5]! & 0x10) ? 10 : 0;
  return 10 + size + footer;
}

function readId3Tags(bytes: Uint8Array): Record<string, string> {
  const tags: Record<string, string> = {};
  readId3v2(bytes, tags);
  readId3v1(bytes, tags); // v1 fills only what v2 did not provide
  return tags;
}

function readId3v2(bytes: Uint8Array, tags: Record<string, string>): void {
  if (bytes.length < 10 || ascii(bytes, 0, 3) !== 'ID3') return;
  const major = bytes[3]!;
  const size = (bytes[6]! << 21) | (bytes[7]! << 14) | (bytes[8]! << 7) | bytes[9]!;
  const frameIdLen = major === 2 ? 3 : 4;
  const frameHeadLen = major === 2 ? 6 : 10;
  const map: Record<string, string> = {
    TIT2: 'title', TPE1: 'artist', TALB: 'album', TYER: 'date', TDRC: 'date',
    TCON: 'genre', TRCK: 'track', TLEN: 'lengthMs',
    TT2: 'title', TP1: 'artist', TAL: 'album', TYE: 'date',
  };

  let pos = 10;
  const end = Math.min(bytes.length, 10 + size);
  while (pos + frameHeadLen <= end) {
    const id = ascii(bytes, pos, frameIdLen);
    if (id === '\0'.repeat(frameIdLen) || id[0] === '\0') break;
    const frameSize = major === 2
      ? (bytes[pos + 3]! << 16) | (bytes[pos + 4]! << 8) | bytes[pos + 5]!
      : major === 4
        ? (bytes[pos + 4]! << 21) | (bytes[pos + 5]! << 14) | (bytes[pos + 6]! << 7) | bytes[pos + 7]!
        : (bytes[pos + 4]! << 24) | (bytes[pos + 5]! << 16) | (bytes[pos + 6]! << 8) | bytes[pos + 7]!;
    if (frameSize <= 0 || pos + frameHeadLen + frameSize > end) break;

    const label = map[id];
    if (label && !(label in tags)) {
      tags[label] = decodeTextFrame(bytes, pos + frameHeadLen, frameSize);
    }
    pos += frameHeadLen + frameSize;
  }
}

function decodeTextFrame(bytes: Uint8Array, start: number, size: number): string {
  const encoding = bytes[start]!;
  const body = bytes.slice(start + 1, start + size);
  let text: string;
  if (encoding === 1 || encoding === 2) {
    // UTF-16 (with or without BOM). TextDecoder handles the BOM.
    text = new TextDecoder(encoding === 1 ? 'utf-16' : 'utf-16be', { fatal: false }).decode(body);
  } else if (encoding === 3) {
    text = new TextDecoder('utf-8', { fatal: false }).decode(body);
  } else {
    text = new TextDecoder('windows-1252', { fatal: false }).decode(body);
  }
  return text.replace(/\0+$/, '').trim();
}

function readId3v1(bytes: Uint8Array, tags: Record<string, string>): void {
  if (bytes.length < 128 || ascii(bytes, bytes.length - 128, 3) !== 'TAG') return;
  const at = bytes.length - 128;
  const field = (off: number, len: number): string =>
    new TextDecoder('windows-1252', { fatal: false })
      .decode(bytes.slice(at + off, at + off + len)).replace(/\0+$/, '').trim();
  if (!tags['title']) tags['title'] = field(3, 30);
  if (!tags['artist']) tags['artist'] = field(33, 30);
  if (!tags['album']) tags['album'] = field(63, 30);
}

/* ------------------------------------------------------------------ *
 * parse
 * ------------------------------------------------------------------ */

/** Walk enough frames to establish duration, bitrate and stream shape. */
export function parseMp3(bytes: Uint8Array): Mp3Info {
  const audioStart = id3v2Length(bytes);
  const tags = readId3Tags(bytes);

  // Find the first real frame at or after the tag, tolerating a few junk bytes.
  let pos = audioStart;
  let first: Frame | null = null;
  const scanLimit = Math.min(bytes.length, audioStart + 0x20000);
  while (pos < scanLimit) {
    const frame = readFrame(bytes, pos);
    // Require a second valid frame right after, to reject a false sync.
    if (frame && readFrame(bytes, pos + frame.length)) { first = frame; break; }
    pos += 1;
  }
  if (!first) {
    // No decodable frames: still a file we opened, but with no playable audio.
    return { durationSec: 0, bitrateKbps: null, variableBitrate: false, sampleRate: null, channels: null, tags, audioStart };
  }

  let totalSamples = 0;
  let frameCount = 0;
  const bitrates = new Set<number>();
  let cursor = first.offset;
  const end = bytes.length - 128 >= 0 && ascii(bytes, bytes.length - 128, 3) === 'TAG'
    ? bytes.length - 128 : bytes.length;

  while (cursor < end) {
    const frame = readFrame(bytes, cursor);
    if (!frame) { cursor += 1; continue; }
    totalSamples += frame.samples;
    bitrates.add(frame.bitrate);
    frameCount += 1;
    cursor += frame.length;
  }

  const durationSec = totalSamples / first.sampleRate;
  const declaredMs = Number(tags['lengthMs']);
  return {
    durationSec: durationSec > 0 ? durationSec : (Number.isFinite(declaredMs) ? declaredMs / 1000 : 0),
    bitrateKbps: bitrates.size === 1 ? [...bitrates][0]! : null,
    variableBitrate: bitrates.size > 1,
    sampleRate: first.sampleRate,
    channels: first.channels,
    tags,
    audioStart: first.offset,
  };
}

/**
 * Cut the audio to `[startSec, endSec]` on whole-frame boundaries.
 *
 * The ID3v2 tag is carried across unchanged, then every frame whose start time
 * falls in the range is copied verbatim. Because frames are independently
 * decodable, the result plays correctly with no re-encoding (§20).
 */
export function trimMp3(bytes: Uint8Array, startSec: number, endSec: number): Uint8Array {
  const info = parseMp3(bytes);
  if (info.durationSec <= 0) {
    throw new HandlerError('error.featuresUnsupported', 'no decodable MP3 frames to trim');
  }
  const start = Math.max(0, Math.min(info.durationSec, startSec));
  const end = Math.max(0, Math.min(info.durationSec, endSec));
  if (end <= start) throw new HandlerError('error.unableToRead', 'the trim range is empty');

  const parts: Uint8Array[] = [];
  // Keep the ID3v2 tag if there is one, so the title survives the trim.
  const tagLen = id3v2Length(bytes);
  if (tagLen > 0) parts.push(bytes.slice(0, tagLen));

  let elapsed = 0;
  let cursor = info.audioStart;
  const streamEnd = ascii(bytes, bytes.length - 128, 3) === 'TAG' ? bytes.length - 128 : bytes.length;
  while (cursor < streamEnd) {
    const frame = readFrame(bytes, cursor);
    if (!frame) { cursor += 1; continue; }
    const frameStart = elapsed;
    elapsed += frame.samples / frame.sampleRate;
    if (frameStart >= start && frameStart < end) {
      parts.push(bytes.slice(frame.offset, frame.offset + frame.length));
    }
    cursor += frame.length;
    if (frameStart >= end) break;
  }

  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const p of parts) { out.set(p, pos); pos += p.length; }
  return out;
}

/* ------------------------------------------------------------------ */

function ascii(bytes: Uint8Array, offset: number, length: number): string {
  if (offset < 0) return '';
  let s = '';
  for (let i = 0; i < length && offset + i < bytes.length; i++) s += String.fromCharCode(bytes[offset + i]!);
  return s;
}
